<script setup lang="ts">
import { computed, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useConfigurator } from '@/composables/configurator/useConfigurator'
import { type MainSkills } from '@/composables/configurator/useVirtualAgentMainSkills'
import type { VirtualAgentRagSettings } from '@/types/virtualAgentRagSettings'
import {
  DEFAULT_GENERATION_MODEL,
  DEFAULT_TRANSCRIPTION_MODEL,
  ASR_MODEL_ARABIC_PREVIEW,
  GENERATION_MODEL_ARABIC_PREVIEW,
} from '@/composables/configurator/useExperimentalOptions.ts'

const { generalInformation, experimental } = useConfigurator()
const route = useRoute()
const router = useRouter()

const onDefaultLanguageChanged = (code: string) => {
  if (code.startsWith('ar')) {
    experimental.value.sampling.generation_model = GENERATION_MODEL_ARABIC_PREVIEW
    experimental.value.transcription_model = ASR_MODEL_ARABIC_PREVIEW
  } else {
    experimental.value.sampling.generation_model = DEFAULT_GENERATION_MODEL
    experimental.value.transcription_model = DEFAULT_TRANSCRIPTION_MODEL
  }
}

const navigateToExperimental = async () => {
  await router.push(route.path.replace(/\/[^/]+\/?$/, '/experimental'))
  await nextTick()
  const el = document.getElementById('custom-skills')
  if (!el) return
  let parent = el.parentElement
  while (parent) {
    const overflow = window.getComputedStyle(parent).overflowY
    if (overflow === 'auto' || overflow === 'scroll') {
      parent.scrollTop = el.offsetTop - parent.offsetTop
      return
    }
    parent = parent.parentElement
  }
}

const mainSkills = computed<MainSkills>({
  get: () => ({
    qualify_call: experimental.value.tools.qualify_call,
    answer_with_rag: experimental.value.tools.answer_with_rag,
    step_validation: experimental.value.tools.step_validation,
    custom_skill: experimental.value.tools.custom_skill,
  }),
  set: ({ qualify_call, answer_with_rag, step_validation, custom_skill }) => {
    experimental.value.tools.qualify_call = qualify_call
    experimental.value.tools.answer_with_rag = answer_with_rag
    experimental.value.tools.step_validation = step_validation
    experimental.value.tools.custom_skill = custom_skill
  },
})

const ragSettings = computed<VirtualAgentRagSettings>({
  get: () => ({
    km_enable_rerank: experimental.value.km_enable_rerank,
    km_embedded_rag: experimental.value.km_embedded_rag,
    km_expand_query: experimental.value.km_expand_query,
    km_similarity_threshold: experimental.value.km_similarity_threshold,
    km_number_of_doc_chunks: experimental.value.km_number_of_doc_chunks,
  }),
  set: ({
    km_enable_rerank,
    km_embedded_rag,
    km_expand_query,
    km_similarity_threshold,
    km_number_of_doc_chunks,
  }) => {
    experimental.value.km_enable_rerank = km_enable_rerank
    experimental.value.km_embedded_rag = km_embedded_rag
    experimental.value.km_expand_query = km_expand_query
    experimental.value.km_similarity_threshold = km_similarity_threshold
    experimental.value.km_number_of_doc_chunks = km_number_of_doc_chunks
  },
})
</script>

<template>
  <GeneralInformation
    v-model="generalInformation"
    v-model:main-skills="mainSkills"
    v-model:rag-settings="ragSettings"
    :custom-skills-enabled="experimental.tools.custom_skill"
    @navigate-to-experimental="navigateToExperimental"
    @default-language-changed="onDefaultLanguageChanged"
    data-testid="gi-tab-content"
  />
</template>
